import React, { useState, useEffect, useCallback } from 'react';
import MainLayout from '../components/MainLayout';
import axios from 'axios';
import { AlertTriangle, Search, Filter } from 'lucide-react';
import { useActiveCompany } from '../hooks/useActiveCompany';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const PayrollExceptions = () => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useActiveCompany();
  const [runs, setRuns] = useState([]);
  const [selectedRun, setSelectedRun] = useState('');
  const [exceptions, setExceptions] = useState([]);

  const fetchCompanies = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/companies`, { withCredentials: true });
      setCompanies(data);
      setSelectedCompany((cur) => (data.length > 0 && !cur ? data[0].id : cur));
    } catch (e) { console.error(e); }
  }, [setSelectedCompany]);

  useEffect(() => { fetchCompanies(); }, [fetchCompanies]);
  const fetchRuns = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/payroll/runs?company_id=${selectedCompany}`, { withCredentials: true });
      setRuns(data);
      if (data.length > 0) setSelectedRun(data[0].id);
    } catch (e) { console.error(e); }
  }, [selectedCompany]);

  useEffect(() => { if (selectedCompany) fetchRuns(); }, [selectedCompany, fetchRuns]);
  const fetchExceptions = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/payroll/runs/${selectedRun}/exceptions`, { withCredentials: true });
      setExceptions(data);
    } catch (e) { console.error(e); }
  }, [selectedRun]);

  useEffect(() => { if (selectedRun) fetchExceptions(); }, [selectedRun, fetchExceptions]);




  return (
    <MainLayout>
      <div data-testid="payroll-exceptions-page" className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Payroll Exceptions</h1>
            <p className="text-sm text-slate-500 mt-1">Review and resolve payroll processing errors</p>
          </div>
          <div className="flex items-center space-x-3">
            <select value={selectedCompany} onChange={(e) => setSelectedCompany(e.target.value)} className="border border-slate-300 rounded-lg px-3 py-2 text-sm">
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <select data-testid="run-selector" value={selectedRun} onChange={(e) => setSelectedRun(e.target.value)} className="border border-slate-300 rounded-lg px-3 py-2 text-sm">
              <option value="">Select Run</option>{runs.map((r) => <option key={r.id} value={r.id}>{`Run #${r.run_number} (${r.status})`}</option>)}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div className="bg-red-50 rounded-xl border border-red-200 p-4 text-center">
            <AlertTriangle className="w-8 h-8 text-red-500 mx-auto mb-2" />
            <p className="text-2xl font-bold text-red-700">{exceptions.length}</p>
            <p className="text-xs text-red-500">Total Exceptions</p>
          </div>
          <div className="bg-amber-50 rounded-xl border border-amber-200 p-4 text-center">
            <p className="text-2xl font-bold text-amber-700">{exceptions.filter(e => e.exceptions?.some(ex => ex.severity === 'high')).length}</p>
            <p className="text-xs text-amber-500">High Severity</p>
          </div>
          <div className="bg-blue-50 rounded-xl border border-blue-200 p-4 text-center">
            <p className="text-2xl font-bold text-blue-700">{exceptions.filter(e => e.exceptions?.some(ex => ex.type === 'negative_net_pay')).length}</p>
            <p className="text-xs text-blue-500">Negative Net Pay</p>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600"><tr>
                <th className="px-4 py-3 text-left font-medium">Employee</th>
                <th className="px-4 py-3 text-left font-medium">Code</th>
                <th className="px-4 py-3 text-left font-medium">Exception</th>
                <th className="px-4 py-3 text-left font-medium">Severity</th>
                <th className="px-4 py-3 text-right font-medium">Net Pay</th>
              </tr></thead>
              <tbody className="divide-y divide-slate-100">
                {exceptions.map((e, i) => (
                  <tr key={i} data-testid={`exception-row-${i}`} className="hover:bg-red-50/30">
                    <td className="px-4 py-3 text-slate-800">{e.employee_name}</td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-600">{e.employee_code}</td>
                    <td className="px-4 py-3">
                      {e.exceptions?.map((ex, j) => (
                        <div key={j} className="text-xs text-red-700 mb-1">{ex.message}</div>
                      ))}
                    </td>
                    <td className="px-4 py-3">
                      {e.exceptions?.map((ex, j) => (
                        <span key={j} className={`px-2 py-0.5 rounded-full text-xs mr-1 ${ex.severity === 'high' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{ex.severity}</span>
                      ))}
                    </td>
                    <td className="px-4 py-3 text-right font-mono">{e.net_pay?.toLocaleString()}</td>
                  </tr>
                ))}
                {exceptions.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">No exceptions found</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default PayrollExceptions;
